<template>
  <div class="zona-pago mt-4">
    <form id="payment-form" @submit.prevent="handlePayment">
      <div class="card pago-container shadow-sm border-0 mx-auto">

        <h5 class="titulo-pago text-center">
          <template v-if="isTrial">🎁 {{ $t('paymentStripe.trialActivate') }}</template>
          <template v-else>🔒 {{ $t('paymentStripe.payWithCard') }}</template>
        </h5>

        <!-- Banner periodo de prueba -->
        <div v-if="isTrial" class="trial-banner">
          <span class="trial-badge">{{ diasGracia === 1 ? $t('paymentStripe.trialDaysSingular') : $t('paymentStripe.trialDaysPlural') }}</span>
          <span class="trial-desc">
            {{ diasGracia }} {{ diasGracia === 1 ? $t('paymentStripe.trialDaysSingular') : $t('paymentStripe.trialDaysPlural') }} · <strong>{{ $t('paymentStripe.trialNoChrge') }}</strong>
          </span>
        </div>

        <div class="label-container mt-3">
          <label class="label-pago">{{ $t('paymentStripe.cardNumber') }}</label>
          <i class="fa fa-credit-card icon-label icon-blue"></i>
        </div>
        <div id="card-number" class="stripe-input"></div>

        <div class="label-container mt-3">
          <label class="label-pago">{{ $t('paymentStripe.expiry') }}</label>
          <i class="fa fa-calendar icon-label icon-green"></i>
        </div>
        <div id="card-expiry" class="stripe-input"></div>

        <div class="label-container mt-3">
          <label class="label-pago">{{ $t('paymentStripe.securityCode') }}</label>
          <i class="fa fa-lock icon-label icon-orange"></i>
        </div>
        <div id="card-cvc" class="stripe-input"></div>

        <p v-if="paymentError" class="error-stripe mt-2">{{ paymentError }}</p>

        <div class="logos-tarjeta text-center">
          <img src="https://img.icons8.com/color/48/visa.png" alt="Visa" />
          <img src="https://img.icons8.com/color/48/mastercard.png" alt="Mastercard" />
          <img src="https://img.icons8.com/color/48/amex.png" alt="Amex" />
        </div>

        <div class="botones-accion mt-4">
          <button
            type="submit"
            class="btn btn-tipster btn-pagar px-4 w-100"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing" class="spinner-border spinner-border-sm"></span>
            <span v-else-if="isTrial">{{ $t('paymentStripe.trialStart') }}</span>
            <span v-else>{{ $t('paymentStripe.pay') }}</span>
          </button>
        </div>

        <p v-if="isTrial" class="trial-note text-center mt-2">
          {{ $t('paymentStripe.trialNote') }}
        </p>

      </div>
    </form>
  </div>
</template>

<script>
import showSwal from "/src/mixins/showSwal.js";

export default {
  props: {
    clientSecret: { type: String, required: true },
    publicKey: { type: String, required: true },
    email: { type: String, required: false, default: "" },
    telefono: { type: String, required: false, default: "" },
    mode: { type: String, default: "payment" },
    referencia: { type: String, default: "" },
    diasGracia: { type: Number, default: 0 },
  },
  data() {
    return {
      stripe: null,
      elements: null,
      cardNumberElement: null,
      cardExpiryElement: null,
      cardCvcElement: null,
      isProcessing: false,
      paymentError: null,
    };
  },
  computed: {
    isTrial() {
      return this.mode === "setup";
    },
  },
  emits: ["cerrar-componente"],
  mounted() {
    this.stripe = window.Stripe(this.publicKey);
    this.elements = this.stripe.elements();

    const style = {
      base: {
        color: "#e7edf5",
        fontFamily: '"Inter", system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": { color: "rgba(231,237,245,0.4)" },
      },
      invalid: { color: "#ff5964", iconColor: "#ff5964" },
    };

    this.cardNumberElement = this.elements.create("cardNumber", { style });
    this.cardExpiryElement = this.elements.create("cardExpiry", { style });
    this.cardCvcElement = this.elements.create("cardCvc", { style });

    this.cardNumberElement.mount("#card-number");
    this.cardExpiryElement.mount("#card-expiry");
    this.cardCvcElement.mount("#card-cvc");
  },
  methods: {
    resetElements() {
      if (this.cardNumberElement) this.cardNumberElement.clear();
      if (this.cardExpiryElement) this.cardExpiryElement.clear();
      if (this.cardCvcElement) this.cardCvcElement.clear();

      this.paymentError = null;
      this.isProcessing = false;
      this.$forceUpdate();
    },

    async handlePayment() {
      this.isProcessing = true;
      this.paymentError = null;

      try {
        if (this.isTrial) {
          await this.handleTrialSetup();
        } else {
          await this.handleRegularPayment();
        }
      } catch (err) {
        console.error("❌ Error general en handlePayment:", err);
        showSwal.methods.showSwal({
          type: "error",
          message: this.$t("paymentStripe.paymentProcessingError"),
          width: 550,
        });
      } finally {
        this.isProcessing = false;
      }
    },

    async handleRegularPayment() {
      const { error, paymentIntent } = await this.stripe.confirmCardPayment(
        this.clientSecret,
        {
          payment_method: {
            card: this.cardNumberElement,
            billing_details: {
              email: this.email,
              phone: this.telefono,
            },
          },
        }
      );

      if (error) {
        this.paymentError = error.message;
        showSwal.methods.showSwal({ type: "error", message: error.message });
        return;
      }

      if (paymentIntent.status === "succeeded") {
        showSwal.methods.showSwal({
          type: "success",
          message: this.$t("paymentStripe.paymentSuccess"),
          width: 550,
        });

        try {
          const response = await this.$store.dispatch("auth/confirmarPagado", {
            clientSecret: this.clientSecret,
            paymentId: paymentIntent.id,
            email: this.email,
            telefono: this.telefono,
          });

          if (response.res === "OK") {
            this.$emit("cerrar-componente", response.res, response.enlace);
          } else {
            this.$emit("cerrar-componente", response.res, response.mensaje);
          }
        } catch (err) {
          console.error("❌ Error al confirmar pago (Stripe OK, backend falló):", err);
          showSwal.methods.showSwal({
            type: "warning",
            message: this.$t("paymentStripe.paymentConfirmedButError"),
            width: 600,
          });
        }
      } else if (paymentIntent.status === "processing") {
        showSwal.methods.showSwal({
          type: "info",
          message: this.$t("paymentStripe.paymentProcessing"),
          width: 550,
        });
      } else {
        this.paymentError = this.$t("paymentStripe.paymentUnexpectedStatus");
      }
    },

    async handleTrialSetup() {
      const { error, setupIntent } = await this.stripe.confirmCardSetup(
        this.clientSecret,
        {
          payment_method: {
            card: this.cardNumberElement,
            billing_details: {
              email: this.email,
              phone: this.telefono,
            },
          },
        }
      );

      if (error) {
        this.paymentError = error.message;
        showSwal.methods.showSwal({ type: "error", message: error.message });
        return;
      }

      if (setupIntent.status === "succeeded") {
        showSwal.methods.showSwal({
          type: "success",
          message: this.$t("paymentStripe.trialSuccess"),
          width: 550,
        });

        const response = await this.$store.dispatch("auth/confirmarTrial", {
          referencia: this.referencia,
          setup_intent_id: setupIntent.id,
        });

        if (response.res === "OK") {
          this.$emit("cerrar-componente", response.res, response.enlace);
        } else {
          this.$emit("cerrar-componente", response.res, response.mensaje);
        }
      }
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 14px;
}
.error-stripe {
  color: var(--q-red);
  font-size: 0.9rem !important;
}
#card-number,
#card-expiry,
#card-cvc {
  border: 1px solid var(--q-border-md);
  background-color: var(--q-hover);
  padding: 10px 12px;
  box-sizing: border-box;
}
#card-number {
  border-radius: 10px;
}
#card-expiry {
  border-radius: 10px;
}
#card-cvc {
  border-radius: 10px;
}

.pago-container {
  max-width: 460px;
  padding: 2rem;
  border-radius: 14px;
  background: var(--q-card);
  border: 1px solid var(--q-border-md);
}

.titulo-pago {
  border: none !important;
  outline: none !important;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--q-gold);
  margin-bottom: 0.5rem;
}

.label-pago {
  font-weight: 600;
  font-size: 0.95rem;
  text-align: left;
  display: block;
}

.icon-input {
  position: relative;
  margin-top: 4px;
}

.icon-izq {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  font-size: 1.2rem;
}

.icon-blue {
  color: var(--q-gold);
}
.icon-green {
  color: var(--q-green);
}
.icon-orange {
  color: var(--q-amber);
}

.logos-tarjeta img {
  width: 36px !important;
  height: auto;
  margin: 0 4px;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  filter: drop-shadow(0 0 0 transparent) !important;
}
.logos-tarjeta {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  filter: drop-shadow(0 0 0 transparent) !important;
}

.btn-pagar span {
  font-weight: 600;
  font-size: 1rem;
  color: #fff;
}
.icon-input {
  width: 100%;
}

.icon-izq {
  z-index: 10;
  left: 14px;
}

.error-stripe {
  color: var(--q-red);
  font-size: 0.9rem;
}

.tarjetas-aceptadas,
.tarjetas-aceptadas * {
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  background: transparent !important;
}

.trial-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(40, 167, 69, 0.12);
  border: 1px solid rgba(40, 167, 69, 0.35);
  border-radius: 10px;
  padding: 10px 14px;
  margin-top: 0.75rem;
}
.trial-badge {
  flex-shrink: 0;
  background: var(--q-green, #28a745);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  border-radius: 6px;
  padding: 2px 8px;
}
.trial-desc {
  font-size: 0.88rem;
  color: var(--q-text, #e7edf5);
  line-height: 1.4;
}
.trial-note {
  font-size: 0.8rem;
  color: var(--q-muted, rgba(231,237,245,0.55));
}

@media (max-width: 600px) {
  .zona-pago {
    margin-top: 0.5rem !important;
  }

  .pago-container {
    padding: 1.25rem 1rem !important;
    border-radius: 10px !important;
    max-width: 100% !important;
  }

  .titulo-pago {
    font-size: 1rem !important;
    margin-bottom: 0.25rem !important;
  }

  .label-pago {
    font-size: 0.88rem !important;
  }

  .label-container {
    margin-top: 0.6rem !important;
  }

  .stripe-input {
    min-height: 46px !important;
    padding: 10px 10px !important;
    border-radius: 8px !important;
  }

  #card-number,
  #card-expiry,
  #card-cvc {
    border-radius: 8px !important;
    width: 100% !important;
    display: block !important;
  }

  .logos-tarjeta {
    margin-top: 0.5rem !important;
    margin-bottom: 0 !important;
  }

  .logos-tarjeta img {
    width: 30px !important;
    margin: 0 3px !important;
  }

  .botones-accion {
    margin-top: 1rem !important;
  }

  .btn-pagar span {
    font-size: 0.95rem !important;
  }

  .icon-izq {
    font-size: 1rem !important;
    left: 10px !important;
  }

  .trial-banner {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 8px 12px;
  }
}
.label-container {
  display: flex;
  align-items: center;
  gap: 6px;
}

.icon-label {
  font-size: 1.1rem;
  opacity: 0.9;
  margin-top: 2px;
}

.stripe-input{
  width:100% !important;
  max-width:100% !important;
  border: 1px solid var(--q-border-md);
  border-radius: 10px;
  background-color: var(--q-hover);
  padding: 10px 12px;
  min-height: 42px;
  box-sizing: border-box;
}
/* Fix iframe size */
.stripe-input iframe {
  width: 100% !important;
}
</style>
